import axios from "axios";
import config from "./config";
import { getAdminAuthToken, redirectToAdminLogin } from "./auth";

// Ensure HttpOnly cookies (token) are stored/sent cross-origin (localhost:3000 -> localhost:3001)
axios.defaults.withCredentials = true;

function buildUrl(endpoint) {
  if (!endpoint) return endpoint;
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = endpoint.replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

function authHeaders() {
  const token = getAdminAuthToken();
  if (!token) return {};
  // Backend expects Bearer token (see apis/middleware/auth.js)
  return { Authorization: `Bearer ${token}` };
}

function isAuthFailure(error) {
  const status = error?.response?.status;
  const msg = String(error?.response?.data?.message || error?.message || "").toLowerCase();
  if (status === 401) return true;
  return (
    msg.includes("missing token") ||
    msg.includes("token is missing") ||
    msg.includes("invalid token") ||
    msg.includes("unauthorized")
  );
}

// Unauthenticated / expired session → clear and go to login (no stuck dashboard).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAuthFailure(error)) {
      redirectToAdminLogin();
    }
    return Promise.reject(error);
  }
);

export function getRequest(endpoint, params) {
  return axios.get(buildUrl(endpoint), { params, headers: authHeaders(), withCredentials: true });
}

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json"
});

export function postRequest(endpoint, data) {
  return axios.post(buildUrl(endpoint), data, {
    headers: jsonHeaders(),
    withCredentials: true
  });
}

export function putRequest(endpoint, data) {
  return axios.put(buildUrl(endpoint), data, {
    headers: jsonHeaders(),
    withCredentials: true
  });
}

export function patchRequest(endpoint, data) {
  return axios.patch(buildUrl(endpoint), data, {
    headers: jsonHeaders(),
    withCredentials: true
  });
}

export function deleteRequest(endpoint, data) {
  return axios.delete(buildUrl(endpoint), { data, headers: authHeaders(), withCredentials: true });
}

/** FormData: do not set Content-Type — axios adds multipart/form-data with the required boundary. */
function formDataConfig() {
  return {
    headers: { ...authHeaders() },
    withCredentials: true
  };
}

export function postRequestFormData(endpoint, formData) {
  return axios.post(buildUrl(endpoint), formData, formDataConfig());
}

export function putRequestFormData(endpoint, formData) {
  return axios.put(buildUrl(endpoint), formData, formDataConfig());
}

export function patchRequestFormData(endpoint, formData) {
  return axios.patch(buildUrl(endpoint), formData, formDataConfig());
}

